/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {DiagnosticTile} from './DiagnosticTile';
import {DiagnosticTileData} from './diagnosticTileConfig';

export interface DiagnosticTileGridProps {
  tiles: DiagnosticTileData[];
}

const useStyles = makeStyles(() => ({
  grid : {
    display   : 'flex',
    flexWrap  : 'wrap',
    width     : '100%',
    rowGap    : '16px',
    columnGap : '16px'
  }
}));

/**
 * DiagnosticTileGrid lays out the ION-7462 Diagnostic Tiles 3-per-row with
 * 16px horizontal/vertical gaps, per the Figma "Diagnostic Tile Layout
 * Flow": tiles are placed left to right, then top to bottom, in `tiles`'
 * own order (callers are expected to have already sorted by the Diagnostic
 * Global Priority order, e.g. via `DIAGNOSTIC_TILE_ORDER`, and to have
 * dropped any field the asset has no data for). A plain `flex-wrap` row at
 * the exact 3-tile container width (424px = 3 * 130.67px + 2 * 16px) gives
 * the "remaining tiles reflow to fill available space" behavior for free -
 * omitting a tile from `tiles` simply closes its slot and everything after
 * it shifts up, with no layout math needed here.
 *
 * ```typescript
 *
 *   import {DiagnosticTileGrid} from
 *     '@ion/dls/src/components/assetdetails/current/DiagnosticTileGrid';
 *   ...
 *
 *   <DiagnosticTileGrid tiles={[
 *     {id: 'seatbelt', label: 'SEAT BELT', value: 'Buckled', severity: 'good'},
 *     {id: 'fuel', label: 'FUEL LEVEL', value: '9', unit: '%', severity: 'critical'}
 *   ]} />
 *
 * ```
 *
 */
export const DiagnosticTileGrid = ({tiles}: DiagnosticTileGridProps) => {
  const classes = useStyles();
  return (
    <div className={classes.grid}>
      {tiles.map(tile => (
        <DiagnosticTile key={tile.id}
          label={tile.label}
          value={tile.value}
          unit={tile.unit}
          severity={tile.severity} />
      ))}
    </div>
  );
};

export default DiagnosticTileGrid;
